import { TZDateMini } from '@date-fns/tz';
import { endOfMonth, isSameMonth, startOfMonth } from 'date-fns';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { formatDate } from '@dynatrace-sdk/units';
import { getTimezone } from '@dynatrace-sdk/user-preferences';

import { isTimeframe, isTimeValue } from '../../../core/time/helpers.js';
import { useCalendarStore } from '../../../core/time/timeframeStore/useTimeframeStore.js';
import { isOutOfBounds } from '../../../core/time/validation.js';
import type { ViewItem } from '../components/CalendarItem.js';
import type { UseCalendarItemProps } from '../types/shared-types.js';
import { useCalendarContext } from './useCalendarContext.js';
import { useFocus } from './useFocus.js';

/** Custom hook that retrieves properties from the current month. */
export function useMonth({ date, itemRef }: UseCalendarItemProps): ViewItem {
  const { state, dispatch } = useCalendarContext();
  const configuredTimezone = getTimezone();
  const value = useCalendarStore((store) => store.calendarValue);
  const min = useCalendarStore((store) => store.min);
  const max = useCalendarStore((store) => store.max);

  const intl = useIntl();

  const ariaLabel = useMemo(() => {
    const formatMessage = intl.formatMessage({
      defaultMessage: 'Select month',
      id: 'aRc3KoQgfpti2E9M',
      description: 'Aria label to add to each month button',
    });
    return `${formatMessage} ${formatDate(date, {
      month: 'long',
    })}`;
  }, [intl, date]);

  const isCurrent = useMemo(
    () => isSameMonth(date, state.today),
    [date, state.today],
  );

  const isFocused = useMemo(
    () => isSameMonth(date, state.activeDate),
    [date, state.activeDate],
  );

  const isSelected = useMemo(() => {
    if (isTimeframe(value, state.type) && value) {
      const { from, to } = value;
      const fromDate = from
        ? new TZDateMini(from.absoluteDate, configuredTimezone)
        : null;
      const toDate = to
        ? new TZDateMini(to.absoluteDate, configuredTimezone)
        : null;

      return !!(
        (fromDate && isSameMonth(fromDate, date)) ||
        (toDate && isSameMonth(toDate, date))
      );
    }

    if (isTimeValue(value)) {
      const selectedDate = value
        ? startOfMonth(new TZDateMini(value.absoluteDate, configuredTimezone))
        : null;

      if (!selectedDate) {
        return false;
      }

      return isSameMonth(selectedDate, date);
    }
  }, [configuredTimezone, value, state.type, date]);

  const { tabIndex } = useFocus({ isFocused, itemRef });

  const isDisabled = useMemo(() => {
    const startDay = startOfMonth(date);
    const endDay = endOfMonth(date);

    return !!(
      isOutOfBounds(startDay, undefined, max) || isOutOfBounds(endDay, min)
    );
  }, [min, max, date]);

  /** Handle click on a month in the month view. */
  function onClick() {
    dispatch({
      type: 'activeDateChange',
      payload: { activeDate: date, changeToView: 'day' },
    });

    dispatch({ type: 'shouldUpdateFocusFromKeydown', payload: false });
  }

  return {
    ariaLabel,
    tabIndex,
    isCurrent,
    isSelected,
    isDisabled,
    onClick,
  };
}
